import mapboxgl from 'mapbox-gl';

import * as turf from '@turf/turf';

import {
  Feature,
  FeatureCollection,
  GeoJsonProperties,
  LineString,
  MultiLineString,
  Point,
  Position,
} from 'geojson';

import { LngLatArrayType, PtLatLng, PtXy, Segment } from '../../../appTypes';
import { arrayEquals } from '../../common/utils';

export function jsonTupleToLatlng(jt: LngLatArrayType | Position) {
  return { lng: jt[0], lat: jt[1] };
}

export function latLngToJsonPoint(ll: PtLatLng): Point {
  return { type: 'Point', coordinates: [ll.lng, ll.lat] };
}

export function jsonPointToLatLng(jp: Point) {
  return jsonTupleToLatlng(jp.coordinates as any);
}

export function launderLatLng(ll: PtLatLng): PtLatLng {
  return { lng: Number(ll.lng), lat: Number(ll.lat) };
}

function beLatLng(pt: PtXy): PtLatLng {
  if ('x' in pt && 'y' in pt) return { lng: pt.x, lat: pt.y };
  return pt as unknown as PtLatLng;
}

export function geojsonToCoordinates(point?: Point): PtLatLng {
  if (point) {
    return { lat: point.coordinates[1], lng: point.coordinates[0] };
  }
  return undefined;
}

function distanceSquaredLatLng(v: PtLatLng, w: PtLatLng) {
  return (v.lng - w.lng) ** 2 + (v.lat - w.lat) ** 2;
}

function distanceLatLng(v: PtLatLng, w: PtLatLng) {
  return Math.sqrt(distanceSquaredLatLng(v, w));
}

export function distanceXy(v: PtXy, w: PtXy) {
  return Math.sqrt(distanceSquaredLatLng(beLatLng(v), beLatLng(w)));
}

function nearestPointOnLineSegmentLatLng(
  p: PtLatLng,
  a: PtLatLng,
  b: PtLatLng,
) {
  // find closest point on segment ab to point p (all points xy)
  const segmentLengthSquared = distanceSquaredLatLng(a, b);
  if (segmentLengthSquared === 0) return a;
  let t =
    ((p.lng - a.lng) * (b.lng - a.lng) + (p.lat - a.lat) * (b.lat - a.lat)) /
    segmentLengthSquared;
  t = Math.max(0, Math.min(1, t));
  return {
    lng: a.lng + t * (b.lng - a.lng),
    lat: a.lat + t * (b.lat - a.lat),
  };
}

function distanceToSegmentLatLng(segment: Segment, pt: PtLatLng) {
  const { coordinates } = segment.geometry;

  let segmentDist: number;
  let segmentPt: PtLatLng;

  // for each leg in segment
  for (let j = 0; j < coordinates.length - 1; j++) {
    const legPt = nearestPointOnLineSegmentLatLng(
      pt,
      jsonTupleToLatlng(coordinates[j]),
      jsonTupleToLatlng(coordinates[j + 1]),
    );
    const legDist = distanceLatLng(legPt, pt);
    if (!segmentDist || segmentDist > legDist) {
      segmentDist = legDist;
      segmentPt = legPt;
    }
  }

  return { segmentPt, segmentDist };
}

export function findNearestSegmentLatLng(
  segments: Segment[],
  position: PtLatLng,
  excludeId: string = undefined,
) {
  // all coordinates latlng
  // console.log(`findNearestSegmentLatLng position: ${JSON.stringify(position)}`);
  // console.log(`findNearestSegmentLatLng ${segments.length} segments`);

  let nearestSegment: Segment;
  let nearestSegmentPointLatLng: PtLatLng;
  let nearestSegmentDistance: number = Number.POSITIVE_INFINITY;
  // for each segment in segments
  for (let i = 0; i < segments.length; i++) {
    const segment = segments[i];
    if (!(excludeId && segment.properties.segment_id === excludeId)) {
      const { segmentPt, segmentDist } = distanceToSegmentLatLng(
        segments[i],
        position,
      );
      // if this is the closest segment we have seen so far, remember it
      if (!nearestSegment || nearestSegmentDistance > segmentDist) {
        nearestSegment = segment;
        nearestSegmentPointLatLng = segmentPt;
        nearestSegmentDistance = segmentDist;
      }
    }
  }

  return { nearestSegment, nearestSegmentPointLatLng };
}

export function geojsonCoordinates(segment) {
  if (segment.coordinates) {
    return segment.coordinates;
  }
  if (segment.features) {
    const ret = [];
    for (let i = 0; i < segment.features.length; i++) {
      ret.push(geojsonCoordinates(segment.features[i]));
    }
    return ret;
  }
  return segment.geometry.coordinates;
}

export function mergeCoordRuns(coords: Position[][]) {
  function pointEq(p1, p2) {
    const margin = 0.00005;
    if (Math.abs(p1[0] - p2[0]) < margin && Math.abs(p1[1] - p2[1]) < margin) {
      return true;
    }
    return false;
  }

  function excludingIndexes(list, i, j) {
    const ret = [];
    for (let it = 0; it < list.length; it++) {
      if (!(it === i || it === j)) {
        ret.push(list[i]);
      }
    }
    return ret;
  }

  function isSubset(c1, c2) {
    if (c1.length > c2.length) {
      return false;
    }
    for (let i = 0; i < c2.length; i++) {
      if (i < c1.length) {
        if (!pointEq(c1[i], c2[i])) {
          return false;
        }
      }
    }
    return true;
  }

  let progress = true;
  while (progress && coords.length > 1) {
    progress = false;
    for (let i = 0; i < coords.length; i++) {
      for (let j = i + 1; j < coords.length; j++) {
        if (pointEq(coords[i][0], coords[j][coords[j].length - 1])) {
          // J -> I
          // console.log('J -> I', i, j);
          const newCord = [...coords[j], ...coords[i]];
          coords = [newCord, ...excludingIndexes(coords, i, j)];
          progress = true;
        } else if (pointEq(coords[j][0], coords[i][coords[i].length - 1])) {
          // I -> J
          // console.log('I -> J', i, j);
          const newCord = [...coords[i], ...coords[j]];
          coords = [newCord, ...excludingIndexes(coords, i, j)];
          progress = true;
        } else if (arrayEquals(coords[i], coords[j])) {
          // I = J
          // console.log('I = J', i, j);
          coords = [...excludingIndexes(coords, j, j)];
          progress = true;
        } else if (isSubset(coords[i], coords[j])) {
          // J is strictly larger, drop I
          // console.log('J >> I', i, j);
          coords = [...excludingIndexes(coords, i, i)];
          progress = true;
        } else if (isSubset(coords[j], coords[i])) {
          // I is strictly larger, drop J
          // console.log('I >> J', i, j);
          coords = [...excludingIndexes(coords, j, j)];
          progress = true;
        }
      }
    }
  }
  return coords;
}

export function mergeSegments(segments) {
  if (!segments || segments.length === 0) {
    return undefined;
  }
  if (segments.length === 1) {
    return segments[0];
  }

  // console.log('Merging multiple segments for drawing', segments);
  let coords = [];

  for (let i = 0; i < segments.length; i++) {
    coords.push(geojsonCoordinates(segments[i]));
  }
  coords = mergeCoordRuns(coords);
  const kingSegment = segments[0];
  const ret = {
    type: kingSegment.type,
    properties: kingSegment.properties,
    geometry: kingSegment.geometry,
    coordinates: kingSegment.coordinates,
  };
  if (kingSegment.coordinates) {
    // eslint-disable-next-line
    ret.coordinates = coords[0];
  } else {
    // eslint-disable-next-line
    ret.geometry.coordinates = coords[0];
  }
  // console.log('Merged segments ', segments, 'to', ret, coords);
  return ret;
}

export type MergedSegmentsCollection = FeatureCollection<
  LineString,
  GeoJsonProperties
> & {
  properties: {
    segment_ids: string[];
  };
};

export function mergeSegmentsBySegmentIds(
  segments,
): MergedSegmentsCollection | undefined {
  if (!segments || segments.length === 0) {
    return undefined;
  }
  const seperate_segs = {};
  for (let i = 0; i < segments.length; i++) {
    const lid = segments[i].properties.segment_id;
    seperate_segs[lid] = seperate_segs[lid] || [];
    seperate_segs[lid].push(segments[i]);
  }

  const seg_groups = Object.values(seperate_segs);
  const features = [];
  for (let j = 0; j < seg_groups.length; j++) {
    const merged = mergeSegments(seg_groups[j]);
    if (merged) {
      features.push(merged);
    }
  }
  return {
    type: 'FeatureCollection',
    properties: {
      segment_ids: Object.keys(seperate_segs),
    },
    features,
  };
}

export function breakMultiLineToLineFeatures(
  feature: Feature<MultiLineString>,
): Feature<LineString>[] {
  const ret: Feature<LineString>[] = [];
  for (let i = 0; i < feature.geometry.coordinates.length; i++) {
    const lsFeature: Feature<LineString> = {
      ...feature,
      geometry: {
        coordinates: feature.geometry.coordinates[i],
        type: 'LineString',
      },
    };
    ret.push(lsFeature);
  }
  return ret;
}

export function bboxFromPointBuffer(
  point: mapboxgl.Point,
  buffer: number,
): [mapboxgl.PointLike, mapboxgl.PointLike] {
  return [
    [point.x - buffer / 2, point.y - buffer / 2],
    [point.x + buffer / 2, point.y + buffer / 2],
  ];
}

export function interpAlongLineFeature(
  feature: Feature<LineString | MultiLineString>,
  distance: number,
): Feature<Point> {
  let fromEnd = false;
  if (distance < 0) {
    // Like python array indexing, negative number means backwards indexing.
    distance = Math.abs(distance);
    fromEnd = true;
  }
  if (feature.geometry.type === 'MultiLineString') {
    let dist_seen = 0;
    let features = breakMultiLineToLineFeatures(
      feature as Feature<MultiLineString>,
    );
    if (fromEnd) {
      features = features.reverse();
    }
    for (let i = 0; i < features.length; i++) {
      const lsFeature = features[i];
      const len = turf.length(lsFeature);
      if (distance >= dist_seen && distance < dist_seen + len) {
        if (!fromEnd) {
          return turf.along(lsFeature, distance - dist_seen);
        } else {
          return turf.along(lsFeature, len - (distance - dist_seen));
        }
      }
      dist_seen += len;
    }
    // Never seen the distance offset on the selected route, return the last point
    const last_geom = features[features.length - 1].geometry;
    return turf.point(last_geom.coordinates[last_geom.coordinates.length - 1]);
  }
  if (!fromEnd) {
    return turf.along(feature as Feature<LineString>, distance);
  } else {
    return turf.along(
      feature as Feature<LineString>,
      turf.length(feature) - distance,
    );
  }
}

export function interpAlongLineFeatureRelative(
  feature: Feature<LineString | MultiLineString>,
  distanceRel: number,
): Feature<Point> {
  // relative 0 - 1
  if (distanceRel < 0 || distanceRel > 1)
    throw new Error('Distance must be between 0 and 1');
  let targetDistance = turf.length(feature) * distanceRel;
  // handle multiline strings
  if (feature.geometry.type === 'MultiLineString') {
    const features = breakMultiLineToLineFeatures(
      feature as Feature<MultiLineString>,
    );
    const totalDistance = features.reduce((sum, f) => sum + turf.length(f), 0);
    targetDistance = totalDistance * distanceRel;
  }
  return interpAlongLineFeature(feature, targetDistance);
}

export function lineStartStub(feature: Feature<LineString | MultiLineString>) {
  return turf.lineString([
    interpAlongLineFeature(feature, 0).geometry.coordinates,
    interpAlongLineFeature(feature, 0.005).geometry.coordinates,
  ]);
}

export function lineEndStub(feature: Feature<LineString | MultiLineString>) {
  return turf.lineString([
    interpAlongLineFeature(feature, -0.006).geometry.coordinates,
    interpAlongLineFeature(feature, -0.001).geometry.coordinates,
  ]);
}

export function lineMidStub(feature: Feature<LineString | MultiLineString>) {
  return turf.lineString([
    interpAlongLineFeatureRelative(feature, 0.48).geometry.coordinates,
    interpAlongLineFeatureRelative(feature, 0.53).geometry.coordinates,
  ]);
}

export function linePerpendicularCap(
  feature: Feature<LineString | MultiLineString>,
  atStart: boolean,
): Feature<LineString> {
  const bearingStub = atStart ? lineStartStub(feature) : lineEndStub(feature);
  const stubCoords = bearingStub.geometry.coordinates;
  const lineBearing = turf.bearing(stubCoords[0], stubCoords[1]);
  const center = atStart
    ? interpAlongLineFeature(feature, 0)
    : interpAlongLineFeature(feature, -0.001);
  const perpBearing = lineBearing + 90;
  const dist = 8;
  const left = turf.destination(center, dist, perpBearing);
  const right = turf.destination(center, dist, perpBearing); //TODO: karolina + 180
  return turf.lineString(
    [left.geometry.coordinates, right.geometry.coordinates],
    { role: 'cap' },
  );
}
